import { type ReactNode } from 'react';
import { Box, Card, CardContent, CardHeader, Typography, type SxProps, type Theme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { typography as t } from '@/theme/tokens';

/** Muted grey shared by all section-header icons. */
export const SECTION_ICON_COLOR = '#666666';

export interface CardSectionHeaderProps {
  /** Title text. */
  title: string;
  /** Leading icon; defaults to the info outline. */
  icon?: ReactNode;
  /** Node rendered right after the title (e.g. a status pill). */
  extra?: ReactNode;
  /** Node rendered on the far right of the header (e.g. a search field). */
  action?: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * Standard section-card header: a muted icon, a title, and optional inline
 * `extra` / far-right `action` slots. Shared by every software-update card so
 * they read as one family.
 */
export function CardSectionHeader({ title, icon, extra, action, sx }: CardSectionHeaderProps) {
  return (
    <CardHeader
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon ?? <InfoOutlinedIcon sx={{ color: SECTION_ICON_COLOR, fontSize: 20 }} />}
            <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
          </Box>
          {extra}
        </Box>
      }
      action={action}
      sx={{ pb: 0, ...sx }}
    />
  );
}

export interface OverviewFact {
  label: string;
  value: ReactNode;
}

export interface OverviewCardProps {
  /** Inline facts, rendered as hairline-separated columns. */
  facts: OverviewFact[];
  /** Optional full-width text shown below the facts, only when truthy. */
  description?: ReactNode;
  /** Card title; defaults to "Overview". */
  title?: string;
  /** Leading icon; defaults to the info outline. */
  icon?: ReactNode;
  /** Extra node rendered after the title (e.g. a status pill). */
  headerExtra?: ReactNode;
}

/**
 * Shared "Overview" card: icon + title header, hairline-separated fact columns,
 * and an optional description rendered below a divider only when present.
 * Used by the rollout and group detail pages so they share one vocabulary.
 */
export function OverviewCard({
  facts,
  description,
  title = 'Overview',
  icon,
  headerExtra,
}: OverviewCardProps) {
  return (
    <Card>
      <CardSectionHeader title={title} icon={icon} extra={headerExtra} />
      <CardContent sx={{ pt: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 1.5 }}>
          {facts.map(({ label, value }) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                px: 2,
                borderLeft: '1px solid',
                borderColor: 'divider',
                '&:first-of-type': { pl: 0, borderLeft: 'none' },
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10.5 }}
              >
                {label}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: 13, fontWeight: t.fontWeight.medium, overflowWrap: 'anywhere' }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>

        {description && (
          <Typography
            variant="body2"
            sx={{
              mt: 1.5,
              pt: 1.25,
              borderTop: '1px solid',
              borderColor: 'divider',
              fontSize: 13,
              color: 'text.secondary',
              overflowWrap: 'anywhere',
            }}
          >
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default OverviewCard;
